// Alpha authentication store for managing user session state.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface AlphaUser {
  accountId: string;
  email: string;
  role: string; // 'alpha' | 'approved' | 'admin'
  createdAt: string;
  lastLoginAt?: string;
  interests?: Record<string, unknown>;
  emailVerified?: boolean;
  emailVerifiedAt?: string;
  verificationRequiredForTrustedFeedback?: boolean;
}

export interface AuthState {
  user: AlphaUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  _hasHydrated: boolean;
  
  // Actions
  setUser: (user: AlphaUser | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  setHasHydrated: (state: boolean) => void;
}

// Role-based permission helpers
export type UserRole = 'alpha' | 'approved' | 'admin';

/**
 * Check if a role can perform actions (close positions, stop replay, etc.)
 * Only 'approved' and 'admin' roles can perform actions.
 * 'alpha' role is view-only.
 */
export function canPerformActions(role: string | undefined): boolean {
  return role === 'approved' || role === 'admin';
}

/**
 * Check if a role has admin privileges
 */
export function isAdmin(role: string | undefined): boolean {
  return role === 'admin';
}

/**
 * Hook to check if current user can perform actions
 */
export function useCanPerformActions(): boolean {
  const user = useAuthStore((state) => state.user);
  return canPerformActions(user?.role);
}

/**
 * Hook to check if current user has admin privileges
 */
export function useIsAdmin(): boolean {
  const user = useAuthStore((state) => state.user);
  return isAdmin(user?.role);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _hasHydrated: false,
      
      setUser: (user) => set({ 
        user, 
        isAuthenticated: !!user,
        error: null,
      }),
      
      setToken: (token) => {
        // Bridge: sync to localStorage 'auth_token' key so that client.ts,
        // metrics.ts, profile.ts, settingsStore, and ~40 other consumers
        // that read localStorage.getItem('auth_token') get the correct value.
        if (token) {
          localStorage.setItem('auth_token', token);
        } else {
          localStorage.removeItem('auth_token');
        }
        set({ token });
      },
      
      setLoading: (isLoading) => set({ isLoading }),
      
      setError: (error) => set({ error, isLoading: false }),
      
      logout: () => {
        localStorage.removeItem('auth_token');
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
          error: null,
        });
      },
      
      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: 'alpha-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // On hydration, sync the persisted token to localStorage 'auth_token'
        // so all legacy consumers (client.ts, metrics.ts, etc.) have it available.
        if (state?.token) {
          localStorage.setItem('auth_token', state.token);
        } else {
          localStorage.removeItem('auth_token');
        }
        state?.setHasHydrated(true);
      },
    }
  )
);

/**
 * Hook to check if the auth store has been hydrated from localStorage
 * Use this to prevent flash of unauthenticated state
 */
export function useAuthHydrated(): boolean {
  return useAuthStore((state) => state._hasHydrated);
}
